/**
 * PartnerBilling — /partner/billing
 *
 * Page de gestion de l'abonnement Partenaire :
 *   - Affiche les add-ons actuellement actifs (depuis users.partnerSubscription)
 *   - Permet d'ajouter / retirer des add-ons
 *   - Avant confirmation : affiche un modal avec preview prorata
 *   - Si aucune subscription active → affiche le sélecteur + bouton Checkout
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useUser } from "@/lib/useUser";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";
import { PartnerServiceSelection } from "@/components/partners/PartnerServiceSelection";
import {
  createPartnerCheckout,
  updatePartnerAddons,
  syncPartnerSubscription,
  PARTNER_ADDON_LABELS,
  PARTNER_ADDON_SLUGS,
  type PartnerAddonSlug,
  type UpdatePartnerAddonsPreview,
} from "@/lib/partner-billing";


interface PartnerSub {
  baseActive: boolean;
  addons: PartnerAddonSlug[];
  status: string;
  monthlyTotal: number;
  currentPeriodEnd?: { seconds: number } | null;
  cancelAtPeriodEnd?: boolean;
  stripeSubscriptionId?: string | null;
}

function fmtMoney(cents: number, currency = "usd") {
  const sign = cents < 0 ? "−" : "";
  const abs = Math.abs(cents) / 100;
  return `${sign}$${abs.toFixed(2)} ${currency.toUpperCase()}`;
}

function fmtDate(seconds?: number | null) {
  if (!seconds) return "—";
  return new Date(seconds * 1000).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function PartnerBilling() {
  const { user, loading: authLoading } = useUser();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const checkoutStatus = searchParams.get("status");

  const [sub, setSub] = useState<PartnerSub | null>(null);
  const [subLoading, setSubLoading] = useState(true);
  const [target, setTarget] = useState<PartnerAddonSlug[]>([]);
  const [selectedTier, setSelectedTier] = useState<'basic' | 'pro' | 'elite'>('pro');
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState<UpdatePartnerAddonsPreview | null>(
    null
  );
  const [syncTimedOut, setSyncTimedOut] = useState(false);
  const [syncAttempt, setSyncAttempt] = useState(0);

  // Firestore subscription en temps réel
  useEffect(() => {
    if (!user?.id) return;
    const ref = doc(db, "users", user.id);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data();
        const ps = (data?.partnerSubscription ?? null) as PartnerSub | null;
        setSub(ps);
        if (ps?.addons) {
          // Pré-coche l'add-on manquant si l'utilisateur a été redirigé
          // depuis le gating /internal-partners/:partnerType.
          const missing = searchParams.get("missing") as PartnerAddonSlug | null;
          const next = new Set<PartnerAddonSlug>(ps.addons);
          if (missing && PARTNER_ADDON_SLUGS.includes(missing)) next.add(missing);
          setTarget(Array.from(next));
        }
        setSubLoading(false);
      },
      (err) => {
        console.warn("[PartnerBilling] snapshot error", err);
        setSubLoading(false);
      }
    );
    return () => unsub();
  }, [user?.id, searchParams]);

  // ─── Webhook-independent finalization ────────────────────────────────
  // Si on revient de Stripe avec ?status=success&session_id=cs_..., on
  // appelle syncPartnerSubscription en boucle (max ~60s) jusqu'à ce que
  // l'abonnement soit actif. Cela débloque la page même si le webhook
  // Stripe n'est pas (encore) branché.
  useEffect(() => {
    if (checkoutStatus !== "success") return;
    const sessionId = searchParams.get("session_id");
    if (!sessionId || !user?.id) return;
    if (sub?.baseActive && sub?.status === "active") return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 20; // ~60s @ 3s
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      setSyncAttempt(attempts);
     try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db, auth } = await import('@/lib/firebase');
        const uid = auth.currentUser?.uid;
        if (!uid) throw new Error('Not authenticated');
        const snap = await getDoc(doc(db, 'users', uid));
        const data = snap.data();
        const partnerSub = data?.partnerSubscription;
        if (partnerSub?.baseActive === true || partnerSub?.status === 'active' || partnerSub?.stripeSubscriptionId) {
          setSub({
  baseActive: true,
  addons: partnerSub.addons || [],
  status: partnerSub.status || 'active',
  monthlyTotal: partnerSub.monthlyTotal || 0,
  currentPeriodEnd: partnerSub.currentPeriodEnd || null,
  cancelAtPeriodEnd: partnerSub.cancelAtPeriodEnd || false,
  stripeSubscriptionId: partnerSub.stripeSubscriptionId || null,
});
          return;
        }
        throw new Error('Not active yet');
      } catch (err) {
        console.warn("[PartnerBilling] sync attempt failed", attempts, err);
      }
      if (!cancelled && attempts < maxAttempts) {
        setTimeout(tick, 3000);
      } else if (!cancelled) {
        setSyncTimedOut(true);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [checkoutStatus, searchParams, user?.id, sub?.baseActive, sub?.status]);

  useEffect(() => {
    if (checkoutStatus !== "success") return;
    if (!sub?.baseActive && !sub?.stripeSubscriptionId) return;
    if (!user?.email || !user?.id) return;

    // Email is now sent by NestJS webhook — no client-side send needed.
    // Just redirect to dashboard after activation is confirmed.
    const timer = setTimeout(() => {
      navigate("/internal-partners", { replace: true });
    }, 2000);

    return () => clearTimeout(timer);
  }, [
    checkoutStatus,
    sub?.baseActive,
    sub?.status,
    user?.email,
    user?.id,
    navigate,
  ]);


  const hasActiveSub = !!sub && sub.baseActive && sub.status === "active";
  const currentAddons = useMemo(() => sub?.addons ?? [], [sub]);

  const targetChanged = useMemo(() => {
    const a = [...currentAddons].sort().join(",");
    const b = [...target].sort().join(",");
    return a !== b;
  }, [currentAddons, target]);

  // ─── Actions ───────────────────────────────────────────────────────────

  async function handleInitialCheckout() {
    if (!user?.id) return;
    setBusy(true);
    try {
      const { getAuth } = await import('firebase/auth');
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error('Not authenticated');

      const idToken = await currentUser.getIdToken(true);

     const res = await fetch(
  `${import.meta.env.VITE_API_URL}/payments/partner-checkout`,
  {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            addons: target,
            tier: selectedTier,
            origin: window.location.origin,
            successPath: '/payment-success?role=partner&step=partner_subscription',
cancelPath: '/partner/plans?canceled=true',
          }),
        }
      );

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(`Checkout failed (${res.status}): ${errText}`);
      }
      const data = await res.json();
      if (data?.url) window.location.href = data.url;
      else throw new Error('No checkout URL returned');
    } catch (err: any) {
      toast({
        title: "Checkout failed",
        description: err?.message ?? "Could not start the checkout session.",
        variant: "destructive",
      });
      setBusy(false);
    }
  }

  async function handlePreview() {
    setBusy(true);
    try {
      const res = await updatePartnerAddons({
        targetAddons: target,
        preview: true,
      });
      if ("unchanged" in res) {
        toast({ title: "No changes to apply." });
        setBusy(false);
        return;
      }
      if ("preview" in res && res.preview) {
        setPreview(res);
        setPreviewOpen(true);
      }
    } catch (err: any) {
      toast({
        title: "Preview failed",
        description: err?.message ?? "Could not compute proration preview.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirmApply() {
    setBusy(true);
    try {
      const res = await updatePartnerAddons({
        targetAddons: target,
        preview: false,
      });
      if ("unchanged" in res) {
        toast({ title: "No changes were applied." });
      } else {
        toast({
          title: "Subscription updated",
          description:
            "Your add-ons have been updated. Stripe will sync momentarily.",
        });
      }
      setPreviewOpen(false);
      setPreview(null);
    } catch (err: any) {
      toast({
        title: "Update failed",
        description: err?.message ?? "Could not update subscription.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  }

  // ─── Rendering ─────────────────────────────────────────────────────────

  if (authLoading || subLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1D314D]" />
      </div>
    );
  }

  if (!user) {
    navigate("/login?next=/partner/billing");
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 py-8 px-4">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-[#1D314D]"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-[#1D314D]">
            Partner Billing
          </h1>
          <span />
        </div>

        {/* Status banner from Stripe redirect */}
        {checkoutStatus === "success" && (
          <Card className="mb-4 border-green-300 bg-green-50">
            <CardContent className="flex items-start gap-3 p-4">
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm font-semibold text-green-900">
                  🎉 Payment successful — Subscription Active!
                </p>
                <p className="text-sm text-green-800 mt-1">
                  Your partner subscription is now active.
                  A confirmation email with your receipt has been sent.
                  Redirecting to your dashboard…
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        {checkoutStatus === "canceled" && (
          <Card className="mb-4 border-amber-300 bg-amber-50">
            <CardContent className="flex items-start gap-3 p-4">
              <AlertCircle className="mt-0.5 h-5 w-5 text-amber-600" />
              <p className="text-sm text-amber-900">
                Checkout was canceled. You can complete it any time below.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Current subscription summary (if any) */}
        {hasActiveSub && (
          <Card className="mb-6 border-[#1D314D]">
            <CardHeader>
              <CardTitle className="text-[#1D314D]">
                Current Subscription
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Status</span>
                <span className="font-medium capitalize">{sub?.status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Monthly total</span>
                <span className="font-bold text-[#1D314D]">
                  ${sub?.monthlyTotal}/mo
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Next billing date</span>
                <span className="font-medium">
                  {fmtDate(sub?.currentPeriodEnd?.seconds)}
                </span>
              </div>
              {sub?.cancelAtPeriodEnd && (
                <p className="rounded bg-amber-50 p-2 text-xs text-amber-800">
                  Subscription is scheduled to cancel at the end of the current
                  period.
                </p>
              )}
              <div className="pt-2">
                <p className="mb-1 text-xs uppercase tracking-wide text-slate-500">
                  Active add-ons
                </p>
                {currentAddons.length === 0 ? (
                  <p className="text-sm italic text-slate-500">
                    No add-ons — base only.
                  </p>
                ) : (
                  <ul className="text-sm">
                    {currentAddons.map((slug) => (
                      <li key={slug}>• {PARTNER_ADDON_LABELS[slug]}</li>
                    ))}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 3-tier pricing display — only when no active subscription */}
        {!hasActiveSub && checkoutStatus !== "success" && (
          <div className="mb-6">
            <h2 className="mb-4 text-xl font-bold text-[#1D314D]">
              Choose your plan
            </h2>
            <div className="grid gap-4 md:grid-cols-3">
              {([
                {
                  key: 'basic' as const,
                  name: 'Basic',
                  price: 99,
                  regular: 149,
                  priceId: 'price_1TczgCCkUJGKtqQAoO94CRJL',
                  badge: null as string | null,
                  features: {
                    en: [
                      '1 role included (chosen at registration)',
                      'Internal partner dashboard',
                      'Client directory',
                      'Secure messaging',
                      'Calendar + Video conferencing',
                    ],
                    fr: [
                      "1 rôle inclus (choisi à l'inscription)",
                      'Dashboard partenaire interne',
                      'Annuaire des clients',
                      'Messagerie sécurisée',
                      'Calendrier + Visioconférence',
                    ],
                  },
                },
                {
                  key: 'pro' as const,
                  name: 'Pro',
                  price: 199,
                  regular: 299,
                  priceId: 'price_1TczhcCkUJGKtqQASkJv7VGg',
                  badge: 'Popular',
                  features: {
                    en: [
                      'All Basic benefits',
                      'Verified badge on public profile',
                      'Shared Data Room',
                      'Mandate analytics',
                      'Visibility on deals in your sector',
                    ],
                    fr: [
                      'Tout Basic +',
                      'Badge vérifié sur profil public',
                      'Data Room partagée',
                      'Analytics mandats',
                      'Visibilité sur les deals de votre secteur',
                    ],
                  },
                },
                {
                  key: 'elite' as const,
                  name: 'Elite',
                  price: 349,
                  regular: 499,
                  priceId: 'price_1TcziFCkUJGKtqQAyp4YLDL0',
                  badge: 'Best Value',
                  features: {
                    en: [
                      'All Pro benefits',
                      'Profile featured on ALL relevant listings',
                      '1 Market Study included/quarter',
                      'Dedicated AcquizGroup support',
                      'Priority on buyer contact requests',
                    ],
                    fr: [
                      'Tout Pro +',
                      'Profil mis en avant sur TOUS les listings',
                      '1 Market Study inclus/trimestre',
                      'Support dédié AcquizGroup',
                      'Priorité sur les demandes de contact acheteurs',
                    ],
                  },
                },
              ]).map((tier) => {
                const isSelected = selectedTier === tier.key;
                return (
                  <button
                    key={tier.key}
                    type="button"
                    onClick={() => setSelectedTier(tier.key)}
                    className={`relative rounded-xl border-2 bg-white p-5 text-left shadow-sm transition ${
                      isSelected
                        ? 'border-[#CEAB65] ring-2 ring-[#CEAB65]/40'
                        : 'border-slate-200 hover:border-[#CEAB65]/60'
                    }`}
                  >
                    {tier.badge && (
                      <span className="absolute top-0 right-0 rounded-bl-lg bg-[#CEAB65] px-3 py-1 text-xs font-bold text-[#1D314D]">
                        {tier.badge}
                      </span>
                    )}
                    <h3 className="text-lg font-bold text-[#1D314D]">{tier.name}</h3>
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="text-2xl font-extrabold text-[#1D314D]">
                        ${tier.price}
                      </span>
                      <span className="text-sm text-slate-500">/mo</span>
                      <span className="text-xs text-slate-400 line-through">
                        ${tier.regular}
                      </span>
                    </div>
                    <ul className="mt-3 space-y-1.5">
                      {tier.features.en.map((f, i) => (
                        <li key={i} className="flex items-start text-xs text-slate-700">
                          <CheckCircle2 className="mr-1.5 mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#CEAB65]" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>

            {/* Add-on note */}
            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
              <h3 className="font-bold text-[#1D314D]">
                Additional Roles — +$49/month per role
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                Already registered with one role? Add another professional
                specialty to your profile using the selector below.
              </p>
            </div>
          </div>
        )}


        {/* Selection card — hide the "Subscribe" selector right after a
            successful Stripe redirect while we wait for the webhook to write
            partnerSubscription. Otherwise users see the same "Continue to
            payment" button as before and think they were sent back to checkout. */}
        {checkoutStatus === "success" && !hasActiveSub ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
              {!syncTimedOut ? (
                <>
                  <Loader2 className="h-6 w-6 animate-spin text-[#1D314D]" />
                  <p className="text-sm font-medium text-slate-700">
                    Activating your partner subscription…
                  </p>
                  <p className="text-xs text-slate-500">
                    We're confirming your payment with Stripe. You'll be
                    redirected to your dashboard automatically.
                  </p>
                  {syncAttempt > 3 && (
                    <p className="text-xs text-slate-400">
                      Attempt {syncAttempt} of 20…
                    </p>
                  )}
                </>
              ) : (
                <>
                  <AlertCircle className="h-6 w-6 text-amber-600" />
                  <p className="text-sm font-medium text-slate-800">
                    Your payment went through, but activation is taking longer
                    than expected.
                  </p>
                  <p className="text-xs text-slate-500">
                    Don't worry — your subscription will be activated within a
                    few minutes. You can continue to your dashboard now.
                  </p>
                  <Button
                    onClick={() => navigate("/internal-partners", { replace: true })}
                    className="mt-2 bg-[#CEAB65] font-bold text-[#1D314D] hover:bg-[#CEAB65]/90"
                  >
                    Continue to dashboard
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1D314D]">
                {hasActiveSub ? "Manage Add-ons" : "Subscribe"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PartnerServiceSelection
                selected={target}
                onChange={setTarget}
                disabled={busy}
                showHeader={false}
                baseTier={sub?.baseTier as 'basic' | 'pro' | 'elite' | undefined}
                footerSlot={
                  <div className="pt-4">
                    {hasActiveSub ? (
                      <Button
                        onClick={handlePreview}
                        disabled={busy || !targetChanged}
                        className="w-full bg-[#CEAB65] font-bold text-[#1D314D] hover:bg-[#CEAB65]/90"
                      >
                        {busy ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Calculating preview…
                          </>
                        ) : !targetChanged ? (
                          "No changes to apply"
                        ) : (
                          "Preview & Confirm Changes"
                        )}
                      </Button>
                    ) : (
                      <Button
                        onClick={handleInitialCheckout}
                        disabled={busy}
                        className="w-full bg-[#CEAB65] font-bold text-[#1D314D] hover:bg-[#CEAB65]/90"
                      >
                        {busy ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Redirecting to checkout…
                          </>
                        ) : (
                          "Continue to payment"
                        )}
                      </Button>
                    )}
                  </div>
                }
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm subscription changes</DialogTitle>
            <DialogDescription>
              Review the proration before applying changes to your subscription.
            </DialogDescription>
          </DialogHeader>

          {preview && (
            <div className="space-y-3 text-sm">
              {preview.hasAddition && (
                <p className="rounded bg-blue-50 p-2 text-blue-900">
                  Adding services → you will be billed the prorated amount
                  immediately for the rest of this billing cycle.
                </p>
              )}
              {preview.hasRemoval && (
                <p className="rounded bg-amber-50 p-2 text-amber-900">
                  Removing services → the service will remain active until the
                  end of the cycle, with no refund.
                </p>
              )}

              <div className="rounded border border-slate-200 p-3">
                <div className="mb-2 flex justify-between font-semibold text-[#1D314D]">
                  <span>Amount due today</span>
                  <span>
                    {fmtMoney(preview.amountDueToday, preview.currency)}
                  </span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Next billing</span>
                  <span>
                    {fmtMoney(preview.nextBillingAmount, preview.currency)} on{" "}
                    {preview.nextBillingDate
                      ? new Date(preview.nextBillingDate).toLocaleDateString()
                      : "—"}
                  </span>
                </div>
              </div>

              {preview.prorationLines.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-500">
                    Detail lines ({preview.prorationLines.length})
                  </summary>
                  <ul className="mt-2 space-y-1">
                    {preview.prorationLines.map((l, i) => (
                      <li key={i} className="flex justify-between">
                        <span className="truncate text-slate-600">
                          {l.description ?? l.priceId ?? "Line"}
                        </span>
                        <span>{fmtMoney(l.amount, preview.currency)}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPreviewOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmApply}
              disabled={busy}
              className="bg-[#CEAB65] font-bold text-[#1D314D] hover:bg-[#CEAB65]/90"
            >
              {busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Applying…
                </>
              ) : (
                "Confirm and apply"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
